import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store, StorePreviewComponent, StoresApiService } from '@client-monorepo/daily-fintech/stores';
import { HorizontalScrollComponent, TitleSummaryComponent } from '@client-monorepo/common/ui-components';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { ActionHandlerService, ActionType } from '@client-monorepo/common/action-handler';
import { rangeCreator } from '@client-monorepo/common/utilities';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';
import { Router } from '@angular/router';

@Component({
  selector: 'daily-stores-all-stores-section',
  standalone: true,
  imports: [
    CommonModule,
    StorePreviewComponent,
    HorizontalScrollComponent,
    TitleSummaryComponent,
    NgxSkeletonLoadingComponent,
    NgxButtonComponent,
  ],
  templateUrl: './all-stores-section.component.html',
  styleUrl: './all-stores-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllStoresSectionComponent implements OnInit {
  groupedStores = signal<Store[][] | undefined>(undefined);
  storeService = inject(StoresApiService);
  actionHandler = inject(ActionHandlerService);
  router = inject(Router);
  selectedStores = input<string[]>([]);
  STORE_COUNT = 17;

  ngOnInit(): void {
    this.getAllStores();
  }

  getAllStores(): void {
    if (this.selectedStores() && this.selectedStores().length === this.STORE_COUNT) {
      this.storeService.getStoresByTitles(this.selectedStores(), 0, this.STORE_COUNT).subscribe({
        next: (data) => {
          if (data.length === this.STORE_COUNT) {
            this.chunkStores(this.sortByTitles(data, this.selectedStores()));
          } else {
            this.getPopularStores();
          }
        },
      });
    } else {
      this.getPopularStores();
    }
  }

  sortByTitles(stores: Store[], titles: string[]): Store[] {
    return stores.sort((a, b) => {
      const indexA = titles.indexOf(a.title);
      const indexB = titles.indexOf(b.title);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return 0;
    });
  }

  getPopularStores(): void {
    this.storeService.getPopularStores(0, this.STORE_COUNT).subscribe({
      next: (data) => {
        this.chunkStores(data);
      },
    });
  }

  chunkStores(stores: Store[]): void {
    const grouped: Store[][] = [];
    for (let i = 0; i < stores.length - 1; i += 2) {
      grouped.push([stores[i], stores[i + 1]]);
    }
    grouped.push([stores[stores.length - 1], {} as Store]);
    this.groupedStores.set(grouped);
  }

  goToAllStoresPage(): void {
    this.actionHandler.handle({
      type: ActionType.REDIRECT,
      payload: {
        url: 'stores/all-stores',
      },
    });
  }

  goToStore(store: Store): void {
    this.router.navigate(['/stores', store.trackingCode]);
  }

  protected readonly rangeCreator = rangeCreator;
}
